import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import type { PaymentMethod } from '../pages/DashboardPage/views/FinancesView/masters/financeMasters';
import {
  getAccessToken,
  getActiveCompanyId,
  buildQuery,
  unwrapError,
  unwrapList,
  unwrapRecord,
  unwrapRecordOrObject,
  getTransportError
} from './apiUtils';

const ROUTES = {
  list:   '/payment_methods_list',
  create: '/payment_methods_create',
  update: '/payment_methods_update',
  remove: '/payment_methods_delete'
};

export type PaymentMethodInput = Omit<PaymentMethod, 'id' | 'dbId'>;

// El SQL de los .avap se compone con '%s': una comilla simple en el texto lo rompería
const clean = (value: string) => value.replace(/'/g, '’').trim();

const mapPaymentMethod = (raw: any): PaymentMethod => ({
  id:           String(raw.code ?? ''),
  dbId:         raw.id != null ? String(raw.id) : '',
  name:         raw.name || '',
  type:         raw.type || '',
  account:      String(raw.account ?? ''),
  requiresIban: raw.requires_iban === true || raw.requires_iban === 'true',
  description:  raw.description || '',
  isActive:     !(raw.is_active === false || raw.is_active === 'false')
});

// Lo usan 8 pantallas a la vez: una sola petición por sociedad mientras la lista esté fresca
const CACHE_MS = 30000;
let cache: { companyId: string; at: number; promise: Promise<PaymentMethod[]> } | null = null;

export const invalidatePaymentMethods = () => { cache = null; };

export const listPaymentMethods = (options: { force?: boolean } = {}): Promise<PaymentMethod[]> => {
  const token = getAccessToken();
  if (!token) return Promise.reject(new Error('Sesión no válida o expirada.'));
  const companyId = getActiveCompanyId();

  if (!options.force && cache && cache.companyId === companyId && Date.now() - cache.at < CACHE_MS) {
    return cache.promise;
  }

  const promise = ObexRequestsHandlerP80
    .get(`${ROUTES.list}${buildQuery({ access_token: token, company_id: companyId })}`)
    .then((res: any) => {
      const transportError = getTransportError(res);
      if (transportError) throw new Error(transportError);
      return unwrapList(res).map(mapPaymentMethod);
    });

  cache = { companyId, at: Date.now(), promise };
  // Un fallo no se queda en caché
  promise.catch(() => { if (cache && cache.promise === promise) cache = null; });
  return promise;
};

export const createPaymentMethod = async (code: string, data: PaymentMethodInput): Promise<PaymentMethod> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.create}${buildQuery({
    access_token:  token,
    company_id:    getActiveCompanyId(),
    code,
    name:          clean(data.name),
    type:          data.type,
    account:       data.account,
    requires_iban: data.requiresIban,
    description:   clean(data.description),
    is_active:     data.isActive
  })}`);
  invalidatePaymentMethods();
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al dar de alta la forma de pago.'));
  return mapPaymentMethod(record);
};

export const updatePaymentMethod = async (dbId: string, data: PaymentMethodInput): Promise<PaymentMethod> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.update}${buildQuery({
    access_token:  token,
    id:            dbId,
    name:          clean(data.name),
    type:          data.type,
    account:       data.account,
    requires_iban: data.requiresIban,
    description:   clean(data.description),
    is_active:     data.isActive
  })}`);
  invalidatePaymentMethods();
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al modificar la forma de pago.'));
  return mapPaymentMethod(record);
};

export const deletePaymentMethod = async (dbId: string): Promise<void> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.remove}${buildQuery({ access_token: token, id: dbId })}`);
  invalidatePaymentMethods();
  // El endpoint responde {message}; un error llega como string plano
  const out = unwrapRecordOrObject(res);
  if (!out || !out.message) throw new Error(unwrapError(res, 'Error al eliminar la forma de pago.'));
};
